// Ledger selection state — one `{ date, row, coach }` shared by the header
// (◆ COACH), the LOG surfaces (grid / day list), the inspector (desktop right
// column) and the mobile bottom sheet.
//
// Desktop: `row` is always set (defaults to 'weight'); the inspector renders
// the selected cell, or the coach digest when `coach` is on.
// Mobile: `row === null && !coach` means the sheet is closed; selecting a
// cell opens it. Switching tabs closes it.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { isoToday } from './day-data';
import type { LedgerRow } from './ledger';
import { useIsDesktop } from './useMediaQuery';

export type LedgerSelection = {
  date: string;
  row: LedgerRow | null;
  coach: boolean;
};

export type LedgerState = LedgerSelection & {
  /** Select a cell; closes the coach digest. */
  select: (date: string, row: LedgerRow) => void;
  /** Change the active date without opening an editor (mobile week strip,
   *  desktop week navigation). Keeps the row on desktop. */
  setDate: (date: string) => void;
  openCoach: () => void;
  closeCoach: () => void;
  toggleCoach: () => void;
  /** Close the mobile sheet (row → null, coach → false). */
  closeSheet: () => void;
};

const LedgerContext = createContext<LedgerState | null>(null);

export function LedgerProvider({ children }: { children: ReactNode }) {
  const desktop = useIsDesktop();
  const [sel, setSel] = useState<LedgerSelection>(() => ({
    date: isoToday(),
    row: desktop ? 'weight' : null,
    coach: false,
  }));

  const select = useCallback(
    (date: string, row: LedgerRow) => setSel({ date, row, coach: false }),
    [],
  );
  const setDate = useCallback(
    (date: string) => setSel((s) => ({ ...s, date, row: desktop ? s.row ?? 'weight' : null })),
    [desktop],
  );
  const openCoach = useCallback(() => setSel((s) => ({ ...s, coach: true })), []);
  const closeCoach = useCallback(() => setSel((s) => ({ ...s, coach: false })), []);
  const toggleCoach = useCallback(() => setSel((s) => ({ ...s, coach: !s.coach })), []);
  const closeSheet = useCallback(() => setSel((s) => ({ ...s, row: null, coach: false })), []);

  const value = useMemo<LedgerState>(
    () => ({ ...sel, select, setDate, openCoach, closeCoach, toggleCoach, closeSheet }),
    [sel, select, setDate, openCoach, closeCoach, toggleCoach, closeSheet],
  );
  return <LedgerContext.Provider value={value}>{children}</LedgerContext.Provider>;
}

export function useLedger(): LedgerState {
  const ctx = useContext(LedgerContext);
  if (!ctx) throw new Error('useLedger must be used inside <LedgerProvider>');
  return ctx;
}

/** Persisted desktop page (`/log` | `/plan` | `/trends`). */
export const PAGE_STORAGE_KEY = 'ledger.page';
export const LEDGER_PAGES = ['/log', '/plan', '/trends'] as const;
export type LedgerPage = (typeof LEDGER_PAGES)[number];

export function readStoredPage(): LedgerPage {
  try {
    const raw = window.localStorage?.getItem(PAGE_STORAGE_KEY);
    return (LEDGER_PAGES as readonly string[]).includes(raw ?? '') ? (raw as LedgerPage) : '/log';
  } catch {
    return '/log';
  }
}

export function storePage(page: string): void {
  if (!(LEDGER_PAGES as readonly string[]).includes(page)) return;
  try {
    window.localStorage?.setItem(PAGE_STORAGE_KEY, page);
  } catch {
    // non-fatal
  }
}
